import { FC, useId, useState } from "react";
import styled from "@emotion/styled";
import { useMutation } from "convex/react";
import { useAppStore } from "@/store";
import { api, Doc } from "@/smart-views/client/api";
import { EditableTitle } from "./blocks/EditableTitle";
import { EditPromptModal } from "./EditPromptModal";
import {
  MDSActionMenu,
  MDSActionMenuItem,
  MDSIcon,
  MDSIconColor,
  MDSIconButton,
  MDSIconButtonSize,
  MDSIconButtonVariant,
  MDSIconKind,
  MDSMenuDropdown,
  MDSMenuDropdownSlotName,
  MDSMenuItemVariant,
  MDSText,
  MDSTextColor,
  MDSTextVariant,
  MDSTextWeight,
  MDSTooltip,
  MDSTooltipPlacement,
} from "@/mds";

type SmartViewBackgroundStateKind = "idle" | "working" | "needsReview" | "failed";

const LATEST_MESSAGE_RELEVANCE_MS = 60_000;

export interface SmartViewBackgroundState {
  state: SmartViewBackgroundStateKind;
  status: Doc<"smartViews">["status"];
  label: string;
  detail: string;
  latestMessage: {
    role: "agent" | "system";
    content: string;
    createdAt: number;
  } | null;
  openWorkItemCount: number;
  activeWorkItemCount: number;
  failedWorkItemCount: number;
  lastUserRequest: string | null;
  errorMessage: string | null;
  runStartedAt: number | null;
  completedAt: number | null;
  failedAt: number | null;
  updatedAt: number;
  activityUpdatedAt: number;
}

interface SmartViewHeaderProps {
  view: Doc<"smartViews">;
  backgroundState: SmartViewBackgroundState;
  isPreviewing: boolean;
  onOpenVersionHistory: () => void;
}

export const SmartViewHeader: FC<SmartViewHeaderProps> = ({
  view,
  backgroundState,
  isPreviewing,
  onOpenVersionHistory,
}) => {
  const { store } = useAppStore();
  const updateTitle = useMutation(api.smartViews.client.updateTitle);
  const updateDescription = useMutation(api.smartViews.client.updateDescription);
  const updatePrompt = useMutation(api.smartViews.client.updatePrompt);
  const recompose = useMutation(api.smartViews.client.recompose);
  const deleteSmartView = useMutation(api.smartViews.client.deleteSmartView);
  const [promptModalOpen, setPromptModalOpen] = useState(false);

  const onDelete = async () => {
    if (!confirm(`Delete "${view.title}"? This can't be undone.`)) return;
    await deleteSmartView({ smartViewId: view.smartViewId });
    store.navigation.goToCollections({ openInSameTab: true });
  };

  return (
    <TopBar>
      <MetaRow>
        <Badge>
          <Dot />
          <MDSText variant={MDSTextVariant.TextXs} weight={MDSTextWeight.SemiBold}>
            SMART VIEW
          </MDSText>
        </Badge>
        <MDSText variant={MDSTextVariant.TextXs} color={MDSTextColor.Tertiary}>
          Updated {formatRelative(view.updatedAt)}
        </MDSText>
        <SmartViewBackgroundIndicator backgroundState={backgroundState} />
      </MetaRow>
      <TitleRow>
        <TitleSlot>
          <EditableTitle
            variant="view"
            value={view.title}
            onSave={next => updateTitle({ smartViewId: view.smartViewId, title: next })}
            placeholder="Untitled Smart View"
            disabled={isPreviewing}
          />
        </TitleSlot>
        <Actions>
          <MDSTooltip config={{ label: "Refresh", placement: MDSTooltipPlacement.Top }}>
            <MDSIconButton
              size={MDSIconButtonSize.Small}
              iconKind={MDSIconKind.Refresh}
              variant={MDSIconButtonVariant.OnChrome}
              isDisabled={isPreviewing}
              aria-label="Refresh Smart View"
              onClick={() => recompose({ smartViewId: view.smartViewId })}
              rounded
            />
          </MDSTooltip>
          <MDSMenuDropdown>
            <MDSIconButton
              slot={MDSMenuDropdownSlotName.Trigger}
              size={MDSIconButtonSize.Small}
              iconKind={MDSIconKind.More}
              variant={MDSIconButtonVariant.OnChrome}
              rounded
            />
            <MDSActionMenu
              aria-label="Smart View actions"
              style={{ minWidth: 200 }}
              slot={MDSMenuDropdownSlotName.Menu}
            >
              <MDSActionMenuItem
                id="edit-prompt"
                variant={MDSMenuItemVariant.OneLine}
                label="Edit prompt"
                icon={MDSIconKind.Compose}
                onClick={() => setPromptModalOpen(true)}
              />
              <MDSActionMenuItem
                id="version-history"
                variant={MDSMenuItemVariant.OneLine}
                label="Version history"
                icon={MDSIconKind.History}
                onClick={onOpenVersionHistory}
              />
              <MDSActionMenuItem
                id="delete-smart-view"
                variant={MDSMenuItemVariant.OneLine}
                label="Delete Smart View"
                icon={MDSIconKind.Trash}
                onClick={onDelete}
              />
            </MDSActionMenu>
          </MDSMenuDropdown>
        </Actions>
      </TitleRow>
      <DescriptionSlot>
        <EditableTitle
          variant="block"
          value={view.description ?? ""}
          onSave={next =>
            updateDescription({
              smartViewId: view.smartViewId,
              description: next,
            })
          }
          placeholder="Add a description..."
          disabled={isPreviewing}
        />
      </DescriptionSlot>
      <EditPromptModal
        isOpen={promptModalOpen}
        scopeLabel="entire Smart View"
        initialValue={view.prompt ?? ""}
        onClose={() => setPromptModalOpen(false)}
        onSave={async next => {
          await updatePrompt({ smartViewId: view.smartViewId, prompt: next });
        }}
      />
    </TopBar>
  );
};

const SmartViewBackgroundIndicator: FC<{
  backgroundState: SmartViewBackgroundState;
}> = ({ backgroundState }) => {
  const hoverCardId = useId();
  const latestMessage = backgroundState.latestMessage;
  const backgroundCopy = getBackgroundCopy(backgroundState);
  const showLatestMessage =
    latestMessage !== null &&
    (backgroundState.state !== "idle" ||
      latestMessage.createdAt >= backgroundState.activityUpdatedAt - LATEST_MESSAGE_RELEVANCE_MS);

  return (
    <BackgroundStateShell>
      <BackgroundStateTrigger
        tabIndex={0}
        draggable={false}
        aria-describedby={hoverCardId}
        aria-label={`${backgroundState.label}: ${backgroundCopy.detail}`}
        $state={backgroundState.state}
      >
        <BackgroundStateIcon $state={backgroundState.state}>
          <MDSIcon
            kind={getBackgroundStateIcon(backgroundState.state)}
            size={11}
            boxSize={16}
            color={MDSIconColor.Inherit}
          />
        </BackgroundStateIcon>
        <BackgroundStateLabel>{backgroundState.label}</BackgroundStateLabel>
      </BackgroundStateTrigger>
      <BackgroundStateHoverCard id={hoverCardId} role="status" data-background-state-hover-card>
        <BackgroundStateHoverHeader>
          <BackgroundStateHoverIcon $state={backgroundState.state}>
            <MDSIcon
              kind={getBackgroundStateIcon(backgroundState.state)}
              size={12}
              boxSize={16}
              color={MDSIconColor.Inherit}
            />
          </BackgroundStateHoverIcon>
          <BackgroundStateHoverTitle>{backgroundState.label}</BackgroundStateHoverTitle>
        </BackgroundStateHoverHeader>

        <BackgroundStateHoverText>{backgroundCopy.detail}</BackgroundStateHoverText>

        {backgroundCopy.work && (
          <BackgroundStateHoverSection>
            <BackgroundStateHoverKicker>{backgroundCopy.workLabel}</BackgroundStateHoverKicker>
            <BackgroundStateHoverText>{backgroundCopy.work}</BackgroundStateHoverText>
          </BackgroundStateHoverSection>
        )}

        {showLatestMessage && latestMessage && (
          <BackgroundStateHoverSection>
            <BackgroundStateHoverKicker>Latest update</BackgroundStateHoverKicker>
            <BackgroundStateHoverText>
              {truncateText(latestMessage.content, 220)}
            </BackgroundStateHoverText>
            <BackgroundStateHoverMeta>
              {formatRelative(latestMessage.createdAt)}
            </BackgroundStateHoverMeta>
          </BackgroundStateHoverSection>
        )}

        <BackgroundStateStats>
          <BackgroundStateStat>
            <BackgroundStateStatValue>
              {backgroundState.activeWorkItemCount}
            </BackgroundStateStatValue>
            <BackgroundStateStatLabel>Active</BackgroundStateStatLabel>
          </BackgroundStateStat>
          <BackgroundStateStat>
            <BackgroundStateStatValue>
              {backgroundState.failedWorkItemCount}
            </BackgroundStateStatValue>
            <BackgroundStateStatLabel>Review</BackgroundStateStatLabel>
          </BackgroundStateStat>
          <BackgroundStateStat>
            <BackgroundStateStatValue>
              {formatRelative(backgroundState.activityUpdatedAt)}
            </BackgroundStateStatValue>
            <BackgroundStateStatLabel>Activity</BackgroundStateStatLabel>
          </BackgroundStateStat>
        </BackgroundStateStats>
      </BackgroundStateHoverCard>
    </BackgroundStateShell>
  );
};

const getBackgroundStateIcon = (state: SmartViewBackgroundStateKind): MDSIconKind => {
  if (state === "working") {
    return MDSIconKind.SpinnerThird;
  }

  if (state === "needsReview") {
    return MDSIconKind.Exclamation;
  }

  if (state === "failed") {
    return MDSIconKind.Danger;
  }

  return MDSIconKind.CheckCircleSolid;
};

interface BackgroundCopy {
  detail: string;
  work: string | null;
  workLabel: string;
}

const getBackgroundCopy = (backgroundState: SmartViewBackgroundState): BackgroundCopy => {
  const work = humanizeBackgroundWork(backgroundState);
  const workLabel = getBackgroundWorkLabel(backgroundState);

  if (backgroundState.state === "working") {
    return {
      detail: getWorkingBackgroundDetail(backgroundState),
      work,
      workLabel,
    };
  }

  if (backgroundState.state === "needsReview") {
    return {
      detail: "Mem needs a little help before it can finish this background work.",
      work,
      workLabel,
    };
  }

  if (backgroundState.state === "failed") {
    return {
      detail: backgroundState.errorMessage ?? "The latest background run failed.",
      work,
      workLabel,
    };
  }

  return {
    detail: "Mem is caught up on this Smart View.",
    work,
    workLabel,
  };
};

const getWorkingBackgroundDetail = (backgroundState: SmartViewBackgroundState): string => {
  if (backgroundState.activeWorkItemCount > 0) {
    const updateCount = formatBackgroundUpdateCount(backgroundState.activeWorkItemCount);

    return `Mem is working through ${updateCount}.`;
  }

  return "Mem is thinking through the latest request.";
};

const getBackgroundWorkLabel = (backgroundState: SmartViewBackgroundState): string => {
  if (backgroundState.state === "working") {
    return "Current work";
  }

  if (backgroundState.state === "needsReview") {
    return "Needs attention";
  }

  if (backgroundState.state === "failed") {
    return "Stopped at";
  }

  return "Recent activity";
};

export const humanizeBackgroundWork = (
  backgroundState: SmartViewBackgroundState
): string | null => {
  const request = backgroundState.lastUserRequest?.trim();
  if (!request) {
    return humanizeBackgroundWorkItemCount(backgroundState);
  }

  const sourceSyncWork = humanizeSourceSyncRequest(request, backgroundState.state);
  if (sourceSyncWork) {
    return sourceSyncWork;
  }

  const noteSourceUpdateWork = humanizeNoteSourceUpdateRequest(request, backgroundState.state);
  if (noteSourceUpdateWork) {
    return noteSourceUpdateWork;
  }

  const noteRelevanceCandidateWork = humanizeNoteRelevanceCandidateRequest(
    request,
    backgroundState.state
  );
  if (noteRelevanceCandidateWork) {
    return noteRelevanceCandidateWork;
  }

  const blockCommentWork = humanizeBlockCommentRequest(request, backgroundState.state);
  if (blockCommentWork) {
    return blockCommentWork;
  }

  return humanizeBackgroundWorkItemCount(backgroundState);
};

const humanizeBackgroundWorkItemCount = (
  backgroundState: SmartViewBackgroundState
): string | null => {
  if (backgroundState.openWorkItemCount === 0) {
    return null;
  }

  if (backgroundState.state === "needsReview") {
    const failedCount = formatBackgroundUpdateCount(backgroundState.failedWorkItemCount);

    return `${upperFirst(failedCount)} needs review.`;
  }

  if (backgroundState.state === "failed") {
    return "Mem stopped while handling background work.";
  }

  if (backgroundState.state === "working") {
    const visibleWorkItemCount =
      backgroundState.activeWorkItemCount || backgroundState.openWorkItemCount;
    const activeCount = formatBackgroundUpdateCount(visibleWorkItemCount);

    return `Working through ${activeCount}.`;
  }

  return null;
};

const formatBackgroundUpdateCount = (count: number): string => {
  return count === 1 ? "one background update" : `${count} background updates`;
};

const humanizeSourceSyncRequest = (
  request: string,
  state: SmartViewBackgroundStateKind
): string | null => {
  if (!request.startsWith("Source sync work item:")) {
    return null;
  }

  const sourceTitles = extractSourceNoteTitles(request);
  const blockTitle = request.match(/Block title:\s*(.+)/)?.[1]?.trim();
  const rawUserAction =
    request.match(/User action:\n([\s\S]*?)\n\nThe Smart View already reflects/)?.[1]?.trim() ??
    request.match(/\n\n([\s\S]*?)\n\nThe Smart View already reflects/)?.[1]?.trim();
  const target =
    sourceTitles.length > 0
      ? formatSourceTitleList(sourceTitles)
      : blockTitle
        ? `the source notes for "${blockTitle}"`
        : "the source notes";
  const userAction = rawUserAction ? humanizeSourceSyncAction(rawUserAction) : null;
  const actionSentence = userAction ? `${upperFirst(userAction)}.` : null;
  const checkSentence = getSourceSyncCheckSentence({
    state,
    target,
    hasSpecificSources: sourceTitles.length > 0,
  });

  return truncateText([actionSentence, checkSentence].filter(Boolean).join(" "), 180);
};

const getSourceSyncCheckSentence = ({
  state,
  target,
  hasSpecificSources,
}: {
  state: SmartViewBackgroundStateKind;
  target: string;
  hasSpecificSources: boolean;
}): string => {
  const purpose = hasSpecificSources ? " for matching updates" : "";

  if (state === "working") {
    return `Mem is checking ${target}${purpose}.`;
  }

  if (state === "needsReview") {
    return `Mem needs help checking ${target}${purpose}.`;
  }

  if (state === "failed") {
    return `Mem stopped while checking ${target}${purpose}.`;
  }

  return `Mem checked ${target}${purpose}.`;
};

const humanizeSourceSyncAction = (action: string): string => {
  const cleaned = stripSourceSyncInstructions(action);
  const patterns: Array<[RegExp, (match: RegExpMatchArray) => string]> = [
    [/^Marked (?:task )?"(.+)" as done\.$/, match => `you marked "${match[1]}" done`],
    [/^Reopened (?:task )?"(.+)"\.$/, match => `you reopened "${match[1]}"`],
    [
      /^(?:Updated|Changed) "(.+)" to "(.+)"\.$/,
      match => {
        return `you changed "${match[1]}" to "${match[2]}"`;
      },
    ],
    [
      /^(?:Edited|Changed) "(.+)" for "(.+)" to "(.+)"\.$/,
      match => {
        return `you changed "${match[1]}" for "${match[2]}" to "${match[3]}"`;
      },
    ],
    [/^Edited the section(?: content)?\.$/, () => "you edited the section"],
    [/^Removed the field "(.+)"\.$/, match => `you removed "${match[1]}"`],
    [
      /^(?:Create or update|Asked Mem to create or update) a note for "(.+)"\.$/,
      match => {
        return `you asked Mem to create or update a note for "${match[1]}"`;
      },
    ],
  ];

  for (const [pattern, format] of patterns) {
    const match = cleaned.match(pattern);
    if (match) {
      return format(match);
    }
  }

  return lowerFirst(cleaned.replace(/[.!?]+$/, ""));
};

const stripSourceSyncInstructions = (action: string): string => {
  return action
    .trim()
    .replace(/\s+Update the underlying note\(s\) if relevant\.$/i, "")
    .replace(/\s+If the source notes need to be updated to reflect this, do that now\.$/i, "")
    .trim();
};

const lowerFirst = (value: string): string => {
  if (!value) {
    return value;
  }

  return `${value[0].toLowerCase()}${value.slice(1)}`;
};

const upperFirst = (value: string): string => {
  if (!value) {
    return value;
  }

  return `${value[0].toUpperCase()}${value.slice(1)}`;
};

const humanizeBlockCommentRequest = (
  request: string,
  state: SmartViewBackgroundStateKind
): string | null => {
  if (!request.startsWith("Block comment work item:")) {
    return null;
  }

  const comment = request
    .match(/Block id: .+\n\n([\s\S]*?)\n\nTreat this as block-level guidance/)?.[1]
    ?.trim();
  if (!comment) {
    return getBlockCommentWorkSummary({ state, comment: null });
  }

  return getBlockCommentWorkSummary({ state, comment });
};

const getBlockCommentWorkSummary = ({
  state,
  comment,
}: {
  state: SmartViewBackgroundStateKind;
  comment: string | null;
}): string => {
  const commentDetail = comment ? `: ${truncateText(comment, 150)}` : ".";

  if (state === "working") {
    return `Applying block feedback${commentDetail}`;
  }

  if (state === "needsReview") {
    return `Block feedback needs review${commentDetail}`;
  }

  if (state === "failed") {
    return `Stopped while applying block feedback${commentDetail}`;
  }

  return `Applied block feedback${commentDetail}`;
};

const humanizeNoteSourceUpdateRequest = (
  request: string,
  state: SmartViewBackgroundStateKind
): string | null => {
  if (!request.startsWith("Note source update work item:")) {
    return null;
  }

  const noteTitle = extractChangedNoteTitle(request);
  const target = noteTitle ? `"${noteTitle}"` : "a changed source note";

  if (state === "working") {
    return `Checking Smart View content against ${target}.`;
  }

  if (state === "needsReview") {
    return `Source update needs review for ${target}.`;
  }

  if (state === "failed") {
    return `Stopped while checking ${target}.`;
  }

  return `Checked Smart View content against ${target}.`;
};

const humanizeNoteRelevanceCandidateRequest = (
  request: string,
  state: SmartViewBackgroundStateKind
): string | null => {
  if (!request.startsWith("Note relevance candidate work item:")) {
    return null;
  }

  const noteTitle = extractChangedNoteTitle(request);
  const target = noteTitle ? `"${noteTitle}"` : "a changed note";

  if (state === "working") {
    return `Checking whether ${target} belongs here.`;
  }

  if (state === "needsReview") {
    return `Note relevance needs review for ${target}.`;
  }

  if (state === "failed") {
    return `Stopped while checking relevance for ${target}.`;
  }

  return `Checked whether ${target} belongs here.`;
};

const extractChangedNoteTitle = (request: string): string | null => {
  return (
    request.match(/Changed note:\s*(.+?)\s+\(note_id:/)?.[1]?.trim() ??
    request.match(/Candidate note:\s*(.+?)\s+\(note_id:/)?.[1]?.trim() ??
    null
  );
};

const extractSourceNoteTitles = (request: string): string[] => {
  const sourceSection = request.match(/Source notes:\n([\s\S]*?)\n\nUser action:/)?.[1];
  if (!sourceSection) {
    return [];
  }

  return sourceSection
    .split("\n")
    .map(line => line.trim())
    .filter(line => line.startsWith("- "))
    .map(line =>
      line
        .replace(/^- /, "")
        .replace(/\s+\(note_id: .+\)$/, "")
        .trim()
    )
    .filter(
      title =>
        title && title !== "No source note ids were provided; infer cautiously from the block."
    );
};

const formatSourceTitleList = (titles: string[]): string => {
  if (titles.length === 1) {
    return `"${titles[0]}"`;
  }

  if (titles.length === 2) {
    return `"${titles[0]}" and "${titles[1]}"`;
  }

  return `"${titles[0]}" and ${titles.length - 1} other source notes`;
};

const truncateText = (value: string, maxLength: number): string => {
  if (value.length <= maxLength) {
    return value;
  }

  return `${value.slice(0, maxLength - 3).trimEnd()}...`;
};

const formatRelative = (timestamp: number): string => {
  const seconds = Math.floor((Date.now() - timestamp) / 1000);
  if (seconds < 60) return "just now";
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes} minute${minutes === 1 ? "" : "s"} ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours} hour${hours === 1 ? "" : "s"} ago`;
  const days = Math.floor(hours / 24);
  return `${days} day${days === 1 ? "" : "s"} ago`;
};

const TopBar = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing["4"]};
  padding: ${({ theme }) => theme.spacing["10"]} ${({ theme }) => theme.spacing["8"]}
    ${({ theme }) => theme.spacing["8"]};
  border-bottom: 1px solid ${({ theme }) => theme.colors.border.default};
`;

const MetaRow = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: ${({ theme }) => theme.spacing["2"]};
`;

const Badge = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  color: ${({ theme }) => theme.colors.text.tertiary};
`;

const Dot = styled.span`
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: #ff6b4a;
  flex-shrink: 0;
`;

const BackgroundStateShell = styled.div`
  position: relative;
  display: inline-flex;
  align-items: center;
  flex-shrink: 0;
  z-index: 10;

  &:hover [data-background-state-hover-card],
  &:focus-within [data-background-state-hover-card] {
    opacity: 1;
    transform: translateY(0);
    pointer-events: auto;
  }
`;

const BackgroundStateTrigger = styled.span<{ $state: SmartViewBackgroundStateKind }>`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  height: 22px;
  padding: ${({ $state }) => ($state === "idle" ? "0" : "0 8px 0 6px")};
  border-radius: 999px;
  border: 1px solid
    ${({ theme, $state }) =>
      $state === "working"
        ? theme.colors.border.emphasis
        : $state === "needsReview" || $state === "failed"
          ? theme.colors.border.input.default.danger
          : "transparent"};
  background: ${({ theme, $state }) =>
    $state === "working"
      ? theme.colors.background.badge.emphasis
      : $state === "needsReview" || $state === "failed"
        ? theme.colors.background.badge.danger
        : "transparent"};
  color: ${({ theme, $state }) =>
    $state === "working"
      ? theme.colors.text.emphasis
      : $state === "needsReview" || $state === "failed"
        ? theme.colors.text.danger
        : theme.colors.text.tertiary};
  cursor: default;
  outline: none;

  &:focus-visible {
    box-shadow: 0 0 0 2px ${({ theme }) => theme.colors.border.emphasis};
  }
`;

const BackgroundStateIcon = styled.span<{ $state: SmartViewBackgroundStateKind }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 16px;
  height: 16px;
`;

const BackgroundStateLabel = styled.span`
  font-size: 11px;
  font-weight: 500;
  line-height: 1;
  white-space: nowrap;
`;

const BackgroundStateHoverCard = styled.div`
  position: absolute;
  top: calc(100% + 8px);
  left: 0;
  z-index: 30;
  width: min(340px, 78vw);
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing["3"]};
  padding: ${({ theme }) => theme.spacing["3"]};
  border: 1px solid ${({ theme }) => theme.colors.border.default};
  border-radius: ${({ theme }) => theme.borderRadius.large};
  background: ${({ theme }) => theme.colors.surface.popover};
  box-shadow: ${({ theme }) => theme.shadows.surface};
  color: ${({ theme }) => theme.colors.text.primary};
  opacity: 0;
  transform: translateY(-3px);
  pointer-events: none;
  cursor: default;
  transition:
    opacity 0.12s ease,
    transform 0.12s ease;

  &::before {
    content: "";
    position: absolute;
    top: -8px;
    left: 0;
    right: 0;
    height: 8px;
  }
`;

const BackgroundStateHoverHeader = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing["2"]};
`;

const BackgroundStateHoverIcon = styled.span<{ $state: SmartViewBackgroundStateKind }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
  border-radius: 999px;
  color: ${({ theme, $state }) =>
    $state === "working"
      ? theme.colors.text.emphasis
      : $state === "needsReview" || $state === "failed"
        ? theme.colors.text.danger
        : theme.colors.text.tertiary};
  background: ${({ theme, $state }) =>
    $state === "working"
      ? theme.colors.background.badge.emphasis
      : $state === "needsReview" || $state === "failed"
        ? theme.colors.background.badge.danger
        : theme.colors.background.badge.default};
`;

const BackgroundStateHoverTitle = styled.div`
  color: ${({ theme }) => theme.colors.text.secondary};
  font-size: 12px;
  font-weight: 600;
  line-height: 1.25;
`;

const BackgroundStateHoverSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 3px;
  min-width: 0;
`;

const BackgroundStateHoverKicker = styled.div`
  color: ${({ theme }) => theme.colors.text.tertiary};
  font-size: 10px;
  font-weight: 600;
  line-height: 1.2;
  text-transform: uppercase;
  letter-spacing: 0.04em;
`;

const BackgroundStateHoverText = styled.div`
  color: ${({ theme }) => theme.colors.text.primary};
  font-size: 12px;
  line-height: 1.35;
  word-break: break-word;
`;

const BackgroundStateHoverMeta = styled.div`
  color: ${({ theme }) => theme.colors.text.tertiary};
  font-size: 11px;
  line-height: 1.25;
`;

const BackgroundStateStats = styled.div`
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: ${({ theme }) => theme.spacing["2"]};
  padding-top: ${({ theme }) => theme.spacing["2"]};
  border-top: 1px solid ${({ theme }) => theme.colors.border.default};
`;

const BackgroundStateStat = styled.div`
  min-width: 0;
`;

const BackgroundStateStatValue = styled.div`
  color: ${({ theme }) => theme.colors.text.secondary};
  font-size: 12px;
  font-weight: 600;
  line-height: 1.25;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const BackgroundStateStatLabel = styled.div`
  margin-top: 2px;
  color: ${({ theme }) => theme.colors.text.tertiary};
  font-size: 10px;
  line-height: 1.2;
  text-transform: uppercase;
  letter-spacing: 0.04em;
`;

const TitleRow = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: ${({ theme }) => theme.spacing["4"]};
`;

const TitleSlot = styled.div`
  flex: 1;
  min-width: 0;
`;

const Actions = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing["2"]};
  flex-shrink: 0;
  padding-top: ${({ theme }) => theme.spacing["2"]};
`;

const DescriptionSlot = styled.div`
  font-size: 15px;
  line-height: 1.65;
  color: ${({ theme }) => theme.colors.text.secondary};
  max-width: 680px;
`;
